import { UserRole } from "../constants";

/**
 * Interface for role-based access control configuration
 */
export interface IRoleAccessConfig {
  /** Roles that have access to this resource */
  access: string[];
  /** Whether to allow access if user has any of the allowed roles */
  requireAll?: boolean;
}

/**
 * Get the current user's role from localStorage
 * @returns The user's role or null if not found
 */
export const getCurrentUserRole = (): string | null => {
  return localStorage.getItem("userRole");
};

/**
 * Check if the current user has access based on role configuration
 * @param config Role access configuration
 * @returns True if user has access, false otherwise
 */
export const hasRoleAccess = (config: IRoleAccessConfig): boolean => {
  const userRole = getCurrentUserRole();

  if (!userRole) {
    return false;
  }

  const { access, requireAll = false } = config;

  if (requireAll) {
    // User must have ALL specified roles (not applicable for single role)
    return access.includes(userRole);
  } else {
    // User must have ANY of the specified roles
    return access.includes(userRole);
  }
};

/**
 * Get a human-readable role name
 * @param role The role string
 * @returns Human-readable role name
 */
export const getRoleDisplayName = (role: string): string => {
  const roleMap: Record<string, string> = {
    [UserRole.ADMIN]: "Administrator",
    [UserRole.MANAGER]: "Manager",
    [UserRole.OPS]: "Operations",
    [UserRole.SALES]: "Sales",
    [UserRole.SUPPORT]: "Support",
    [UserRole.USER]: "User",
  };

  return roleMap[role] || role;
};

